import styled from 'styled-components'
// @ts-ignore
import { Scrollama, Step } from 'react-scrollama'
import { TextWidth } from '@ta-interaktiv/react-disco-grid'
import ExampleComponent from '../ExampleComponent'
import { useEffect, useState, useRef } from 'react'
import * as d3 from 'd3'
import Slider from '../Slider'
import { useSpring } from '@react-spring/web'
import { Small, ContentWidth } from '../../theme/styles'
// import { throttle } from 'throttle-debounce'

const AgesChart = () => {
  const timerInterval = useRef<number>()
  const [step, setStep] = useState<number>(0)
  const [stepProgress, setStepProgress] = useState<number>(0)
  const [data, setData] = useState<CsvRow[]>([])
  const [retiredPctData, setRetiredPctData] = useState<RetiredPctData[]>([])
  const [selectedYear, setSelectedYear] = useState<number>(1971)
  const [year, setYear] = useState<number>(1950)
  const [animatedYear, setAnimatedYear] = useState<number>(1950)
  const userInteracted = useRef(false)
  // const throttledHandleScroll = useCallback(throttle(50, handleProgress), [])

  // Throttle the year change. We want a smooth transition only when
  // there is a huge gap between two years
  const thresholdTime = 300
  const lastUpdate = useRef({ year: 1950, time: Date.now() })

  const isRapidChange = (newYear: number) => {
    const now = Date.now()
    const timeElapsed = now - lastUpdate.current.time
    const yearChange = Math.abs(newYear - lastUpdate.current.year)

    if (timeElapsed < thresholdTime && yearChange > 2) {
      return true
    }

    lastUpdate.current = { year: newYear, time: now }
    return false
  }

  const yearSpring = useSpring({
    year: year,
    immediate: !isRapidChange(year),
    onChange: (y) => {
      setAnimatedYear(Math.round(y.value.year))
    },
  })

  // Gives the scrolled percentage of the current step
  useEffect(() => {
    if (step === 2) {
      setYear(1950)
    }

    if (step === 3) {
      setYear(1980 + Math.round(stepProgress * 50))
    }

    if (step === 4) {
      setYear(2030)
    }

    if (step === 5) {
      setYear(1950 + Math.round(Math.min(1, stepProgress * 1.65) * 100))
    }

    if (step === 6) {
      setYear(2050)
    }
  }, [stepProgress])

  useEffect(() => {
    step > 1 && clearInterval(timerInterval.current)
    step === 2 && setYear(1996)
  }, [step])

  useEffect(() => {
    if (userInteracted.current) {
      clearInterval(timerInterval.current)
      setYear(selectedYear)
    } else if (selectedYear !== 1971) {
      userInteracted.current = true
    }
  }, [selectedYear])

  useEffect(() => {
    // Age data
    d3.csv<CsvRow>(
      `${
        process.env.NODE_ENV === 'production'
          ? 'https://dev-interaktiv-mf.tagesanzeiger.ch/2024/votations-federales-avs'
          : ''
      }/data.csv`,
      (d) => {
        return {
          year: +d['year'],
          age: +d['age'],
          count: +d['scenario_a_ref'],
          countHigh: +d['scenario_b_high'],
        }
      }
    )
      .then((data) => {
        setData(data)
      })
      .catch((error) => {
        console.error('Error fetching the CSV data:', error)
      })

    // Retired PCT data
    d3.csv<RetiredPctData>(
      `${
        process.env.NODE_ENV === 'production'
          ? 'https://dev-interaktiv-mf.tagesanzeiger.ch/2024/votations-federales-avs'
          : ''
      }/retired_pct_data.csv`,
      (d) => {
        return {
          year: +d['year'],
          pct: +d['pct_retired'],
        }
      }
    )
      .then((data) => {
        setRetiredPctData(data)
      })
      .catch((error) => {
        console.error('Error fetching the CSV data:', error)
      })

    // Auto animate
    timerInterval.current = window.setInterval(() => {
      setYear((currentYear) => {
        const y = currentYear + 1 > 2050 ? 1950 : currentYear + 1
        return y
      })
    }, 100)

    return () => clearInterval(timerInterval.current)
  }, [])

  const onStepEnter = (stepData: any) => {
    console.log('Enter step', data)
    console.log(stepData)
    setStep(stepData.data)
  }

  const onStepProgress = (stepData: any) => {
    setStepProgress(stepData.progress)
  }

  return (
    <Scrollyrtelling>
      <StickyChart>
        <ContentWidth>
          <>
            <YearTitle>Année</YearTitle>
            <Year>{animatedYear}</Year>
            {process.env.NODE_ENV !== 'production' && (
              <div style={{ position: 'fixed', top: 0, left: 0 }}>
                [{step} - {stepProgress.toFixed(2)}]
              </div>
            )}
            <SliderContainer>
              <SliderContent>
                <Slider
                  min={1950}
                  max={2050}
                  step={1}
                  defaultValue={2000}
                  onChange={(value) => {
                    setSelectedYear(value)
                  }}
                />
              </SliderContent>
            </SliderContainer>
            <ExampleComponent data={data} year={animatedYear} step={step} />
          </>
        </ContentWidth>
        <TextWidth>
          <Small>
            <strong>Source</strong>: Office fédéral de la statistique
          </Small>
        </TextWidth>
      </StickyChart>
      <ScrollytellingContainer>
        <Scrollama
          offset={1}
          onStepEnter={onStepEnter}
          onStepProgress={onStepProgress}
          on
        >
          <Step data={0} key={`scrollama-step-${0}`}>
            <StepContainer style={{ height: '25vh' }} />
          </Step>
          <Step data={1} key={`scrollama-step-${1}`}>
            <StepContainer>
              <StepContent>
                {/* <p>
                  L'évolution démographique du pays est scrutée par l'Office
                  fédéral de la statistique (OFS).
                </p> */}
                <p>
                  L'évolution de la démographie illustre cette tendance à la
                  hausse du nombre de retraités.
                </p>
                <p>
                  Celle-ci nous donne des indications sur la structure de la
                  population résidante permanente en Suisse.
                </p>
              </StepContent>
            </StepContainer>
          </Step>
          <Step data={2} key={`scrollama-step-${2}`}>
            <StepContainer>
              <StepContent>
                <p>
                  Elle permet aussi d'étudier des phénomènes passés. Comme ce
                  creux déjà visible en 1950.
                </p>
                <p>
                  L'empreinte de la grippe espagnole dans la démographie
                  helvétique.
                </p>
              </StepContent>
            </StepContainer>
          </Step>
          <Step data={3} key={`scrollama-step-${3}`}>
            <StepContainer>
              <StepContent>
                <p>
                  Actuellement, la pyramide des âges est dominée par les{' '}
                  <Strong>baby-boomers</Strong>, nés entre <strong>1946</strong>{' '}
                  et <strong>1964</strong>.
                </p>
                <p>
                  La retraite a déjà commencé pour une partie de cette
                  génération. Mais c’est en 2029 que la crête de la vague
                  franchira les 65 ans. Entraînant avec elle un nombre
                  historique de retraités.
                </p>
              </StepContent>
            </StepContainer>
          </Step>
          <Step data={4} key={`scrollama-step-${4}`}>
            <StepContainer>
              <StepContent>
                Une fois cette première vague passée, une accalmie d'une dizaine
                d'année est attendue. Suivie d'une{' '}
                <Strong>deuxième vague</Strong> constituée principalement
                d'immigrants français, allemands et italiens.
              </StepContent>
            </StepContainer>
          </Step>
          <Step data={5} key={`scrollama-step-${5}`}>
            <StepContainer>
              <StepContent>
                <p>
                  Ces vagues successives, associées à l'augmentation de
                  l'espérance de vie, grossissent année après année le
                  contingent d'aînés dans la population.
                </p>
                <p>
                  Celui-ci ne cesse d'augmenter: il{' '}
                  {year < 2024 ? 'était' : year === 2024 ? 'est' : 'sera'} de{' '}
                  <InlinePct>
                    {/* <InlinePctBar style={{ width: `${retiredPctData
                    .find((d) => d.year === year)
                    ?.pct.toFixed(1) ?? 0}%` }} /> */}
                    {retiredPctData
                      .find((d) => d.year === year)
                      ?.pct.toFixed(1).replace('.', ',') ?? 0}
                    %
                  </InlinePct>{' '}
                  en <strong>{year}</strong>{' '}selon le scénario de référence.
                </p>
              </StepContent>
            </StepContainer>
          </Step>
          <Step data={6} key={`scrollama-step-${6}`}>
            <StepContainer>
              <StepContent>
                <p>
                  l'Office fédéral de la statistique (OFS) élabore plusieurs
                  projections.
                </p>
                <p>
                  Son <Strong>scénario «haut»</Strong> combine par exemple un
                  choix d’hypothèses plus favorables à la croissance
                  démographique.
                </p>
              </StepContent>
            </StepContainer>
          </Step>
        </Scrollama>
      </ScrollytellingContainer>
    </Scrollyrtelling>
  )
}

const StickyChart = styled.div`
  position: sticky;
  top: 0;
  left: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100vh;
`

const Scrollyrtelling = styled.div`
  margin-bottom: 20px;

  ${({ theme }) => theme.breakpoints.down('md')} {
    margin-bottom: 20px;
  }
`

const ScrollytellingContainer = styled.div`
  position: relative;
  width: 100%;
  pointer-events: none;
  margin-top: -120vh;
`

const StepContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  padding-bottom: 100vh;
`

const StepContent = styled.div`
  max-width: 300px;
  padding: 15px;
  background: ${({ theme }) => theme.backgroundColor};
  line-height: 1.5;
  font-family: var(--font-serif);
  font-size: 0.95em;
  -webkit-font-smoothing: antialiased;

  ${({ theme }) => theme.breakpoints.up('xxxl')} {
    transform: translateX(-100%);
  }

  p:first-child {
    margin-top: 0;
  }
`

const SliderContent = styled.div`
  width: 400px;
`

const InlinePct = styled.strong`
  color: ${({ theme }) => `rgb(${theme.mainColor})`};
  background: ${({ theme }) => `rgb(${theme.mainColor}, 0.2)`};
  padding: 0 0.25em;
  position: relative;
  font-family: var(--font-sans-serif);
`

const InlinePctBar = styled.span`
  position: absolute;
  display: block;
  top: 0;
  left: 0;
  height: 100%;
  background: ${({ theme }) => `rgb(${theme.mainColor})`};
`

const SliderContainer = styled.div`
  display: flex;
  justify-content: center;
`

const YearTitle = styled.div`
  font-size: 0.75em;
  text-align: center;
  font-weight: 200;
  text-transform: uppercase;
  opacity: 0.5;
  margin-bottom: 0.5em;
  letter-spacing: 0.1em;
`

const Year = styled.div`
  font-size: 3em;
  text-align: center;
  font-weight: bold;
  margin-bottom: 0.5em;
  color: ${({ theme }) => `rgb(${theme.mainColor})`};
`

const Strong = styled.strong`
  color: ${({ theme }) => `rgb(${theme.mainColor})`};
  font-family: var(--font-sans-serif);
  background: ${({ theme }) => `rgb(${theme.mainColor}, 0.2)`};
  padding: 0 0.25em;
`

export default AgesChart
